import { subscriptionEvents } from "@Context/web-portal/subscriptions/subscription-events";
import { useApi, useGlobalContext } from "@hooks/index";
import { Api } from "@Shared/schemas";
import { StripeSubscriptionDetails, StripeUpdateSubscriptionRequest } from "@Shared/schemas/stripe/stripeSchemas";
import { useCallback, useEffect, useState } from "react";

export const useSubscription = () => {
  const { globalState } = useGlobalContext();
  const accountStripeCustomerId = globalState.account.stripe_customer_id;
  const [currentSubscription, setCurrentSubscription] = useState<StripeSubscriptionDetails | null>(null);
  const [updateSubscriptionLoading, setUpdateSubscriptionLoading] = useState(false);

  const {
    call: getSubscriptions,
    loading: subscriptionsLoading,
    data: subscriptions,
  } = useApi<typeof Api.Stripe.GetSubscriptionDetails>({
    api: Api.Stripe.GetSubscriptionDetails,
    request: {
      user_id: accountStripeCustomerId?.toString() || "",
    },
  });

  const { call: updateSubscriptionCall } = useApi<typeof Api.Stripe.UpdateSubscription>({
    api: Api.Stripe.UpdateSubscription,
    request: {
      subscription_id: "",
    },
  });

  const updateSubscription = useCallback(
    async (data: StripeUpdateSubscriptionRequest) => {
      setUpdateSubscriptionLoading(true);
      await updateSubscriptionCall(data);
      setUpdateSubscriptionLoading(false);
    },
    [updateSubscriptionCall]
  );

  useEffect(() => {
    if (accountStripeCustomerId === null) {
      return;
    }

    getSubscriptions();

    const unsubscribe = subscriptionEvents.onSubscriptionChange(() => {
      getSubscriptions();
    });

    return unsubscribe;
  }, [accountStripeCustomerId]);

  useEffect(() => {
    if (subscriptions) {
      // Sort subscriptions by creation date (newest first)
      const sortedSubscriptions = [...subscriptions].sort((a, b) => (b.created || 0) - (a.created || 0));

      // Find the first subscription that is not canceled or incomplete_expired
      setCurrentSubscription(
        sortedSubscriptions.find((sub: StripeSubscriptionDetails) => ["active", "trialing", "past_due"].includes(sub.status)) || null
      );
    }
  }, [subscriptions]);

  const hasActiveSubscription = Boolean(currentSubscription);

  return {
    currentSubscription,
    subscriptions,
    subscriptionsLoading,
    hasActiveSubscription,
    updateSubscription,
    updateSubscriptionLoading,
  };
};

export default useSubscription;
